import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@shared/utils/cn';

interface PrivateProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Render as `<span>` instead of `<div>`. Use when wrapping inline content
   * (e.g. a name inside a heading) where a block-level element would break
   * layout. Default `false` (renders a div).
   */
  inline?: boolean;
  /**
   * Render with `display: contents` instead of `block`. Use when wrapping a
   * flex/grid child group where the children must remain direct flex/grid
   * items. The wrapper element still emits `data-private` / `data-sensitive`
   * (the attributes cascade), but visually the wrapper is layout-invisible.
   *
   * Mutually exclusive with `inline`. Default `false`.
   *
   * `display: contents` has historical screen-reader caveats — fine here
   * because the wrapper carries no semantics (no role, no aria-label).
   */
  asContents?: boolean;
}

/**
 * Wrap PII-collecting or PII-displaying DOM in this component to prevent
 * session-replay tools (FullStory, LogRocket, Hotjar, etc.) from recording
 * the text content.
 *
 * Mechanism: emits both `data-private` (FullStory's standard attribute) and
 * `data-sensitive` (alias that other recorders sometimes honor) on the
 * wrapper. The attributes cascade to all children, so wrapping a region of
 * inputs is equivalent to marking each input individually.
 *
 * The attributes are inert to all non-recorder code — React, the browser,
 * native form submission, MercadoPago SDK, Stripe SDK, browser autofill,
 * and accessibility tooling all ignore them.
 *
 * When to use:
 *   - Any input collecting name, email, phone, address, ID, OTP, password
 *   - Any input collecting card data (PAN, CVC, expiry) — PCI-DSS
 *   - Any region rendering authenticated user info (profile sections)
 *   - Any user-typed special requests / allergies (potentially health info)
 *
 * When NOT to use:
 *   - Diners count, dates, times, restaurant names (not PII)
 *   - Already-tokenized values (post-tokenization card masks like "•••• 4242")
 *
 * Usage:
 * ```tsx
 * <Private>
 *   <input value={cardNumber} onChange={...} />
 * </Private>
 *
 * <Private inline>{userEmail}</Private>
 * ```
 *
 * Documented in DESIGN.md §16 (Privacy). Recording works at the DOM layer,
 * so masking MUST happen at the component layer — server-side scrubbing
 * cannot retroactively remove already-ingested replay data.
 */
export const Private = forwardRef<HTMLDivElement, PrivateProps>(
  ({ children, className, inline = false, asContents = false, ...rest }, ref) => {
    const Tag = inline ? 'span' : 'div';
    // Display class selection. `contents` wins when both are requested
    // (rare) — flex/grid layout preservation usually matters more than
    // inline semantics.
    const displayClass = asContents
      ? 'contents'
      : inline
        ? 'inline'
        : 'block';
    return (
      <Tag
        ref={ref as React.Ref<HTMLDivElement & HTMLSpanElement>}
        data-private="true"
        data-sensitive="true"
        className={cn(displayClass, className)}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

Private.displayName = 'Private';
